import { useCallback, useEffect, useRef, useState } from "react";

export const useSetResponsiveElementsDisplay = () => {
  const expandableTextRef = useRef<HTMLParagraphElement | null>(null);
  const tagsWrapperRef = useRef<HTMLDivElement | null>(null);

  const [expandableTextSettings, setExpandableTextSettings] = useState<{
    isExpanded: boolean;
  } | null>(null);
  const [shownTagsSettings, setShownTagsSettings] = useState<{
    total: number;
    rightPosition: number;
  } | null>(null);

  const checkAndSetTagsToHide = () => {
    if (tagsWrapperRef?.current?.children) {
      // width of the text "+ X", where X is the number of tags that don't fit in tagsWrapper
      const HIDDEN_TAGS_TEXT_WIDTH = 50;
      const tagsWrapperRect = tagsWrapperRef.current.getBoundingClientRect();
      const tags = tagsWrapperRef.current.children;
      let firstTagToHideIndex = 0;
      let lastTagToShowRightPosition = 0;

      while (firstTagToHideIndex < tags.length) {
        const tagRect = tags[firstTagToHideIndex]?.getBoundingClientRect();
        // for the last tag, we must check if it fits in tagsWrapper without the "+ X" text
        const shouldConsiderHiddenTagsText = firstTagToHideIndex < tags.length - 2;

        if (tagsWrapperRect.right - (shouldConsiderHiddenTagsText ? HIDDEN_TAGS_TEXT_WIDTH : 0) < tagRect.right) {
          break;
        }

        lastTagToShowRightPosition = tagRect.right;
        firstTagToHideIndex++;
      }

      setShownTagsSettings({
        total: Math.max(firstTagToHideIndex, 1),
        rightPosition: lastTagToShowRightPosition && tagsWrapperRect.right - lastTagToShowRightPosition,
      });
    }
  };

  const checkAndSetExpandableText = () => {
    if (expandableTextRef?.current) {
      // consider text as expandable if it has been cropped
      if (expandableTextRef.current.scrollHeight > expandableTextRef.current.clientHeight) {
        setExpandableTextSettings({
          isExpanded: !!expandableTextSettings?.isExpanded,
        });
      } else {
        setExpandableTextSettings(null);
      }
    }
  };

  const handleWindowResize = () => {
    checkAndSetExpandableText();
    checkAndSetTagsToHide();
  };

  const setTagsWrapperRef = useCallback((node: null | HTMLDivElement) => {
    if (node) {
      tagsWrapperRef.current = node;
      checkAndSetTagsToHide();
    }
  }, []);

  const setExpandableTextRef = useCallback((node: null | HTMLParagraphElement) => {
    if (node) {
      expandableTextRef.current = node;
      checkAndSetExpandableText();
    }
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleWindowResize);

    return () => window.removeEventListener("resize", handleWindowResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    setExpandableTextRef,
    setTagsWrapperRef,
    expandableTextSettings,
    shownTagsSettings,
    toggleExpandableText: () =>
      setExpandableTextSettings({
        isExpanded: !expandableTextSettings?.isExpanded,
      }),
  };
};
